import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ConnectionState, Room, RoomEvent, Track } from 'livekit-client';
import { fetchLiveKitToken } from '../../utils/api';
import { getPlayerId } from '../../utils/playerIdentity';
import './VoiceChat.css';

const SILENT_WAV = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};
const VOICE_LAUNCHER_STORAGE_KEY = 'ace_voice_launcher_position';
const VOICE_LAUNCHER_SIZE = 46;
const VOICE_LAUNCHER_MARGIN = 8;
const GAME_EDGE_MARGIN = 4;
const GAME_LANDSCAPE_TRAY_WIDTH = 192;

type VoiceLauncherEdge = 'top' | 'right' | 'bottom' | 'left';

interface VoiceLauncherPosition {
  x: number;
  y: number;
  edge: VoiceLauncherEdge;
}

interface VoiceLauncherBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const getLauncherBounds = (screen: 'lobby' | 'game'): VoiceLauncherBounds => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const isLandscape = width > height;

  if (screen === 'game') {
    const tableWidth = isLandscape ? Math.max(width - GAME_LANDSCAPE_TRAY_WIDTH, width * 0.6) : width;
    return {
      minX: GAME_EDGE_MARGIN,
      maxX: Math.max(GAME_EDGE_MARGIN, tableWidth - VOICE_LAUNCHER_SIZE - GAME_EDGE_MARGIN),
      minY: GAME_EDGE_MARGIN,
      maxY: Math.max(GAME_EDGE_MARGIN, height - VOICE_LAUNCHER_SIZE - GAME_EDGE_MARGIN),
    };
  }

  return {
    minX: VOICE_LAUNCHER_MARGIN,
    maxX: Math.max(VOICE_LAUNCHER_MARGIN, width - VOICE_LAUNCHER_SIZE - VOICE_LAUNCHER_MARGIN),
    minY: VOICE_LAUNCHER_MARGIN,
    maxY: Math.max(VOICE_LAUNCHER_MARGIN, height - VOICE_LAUNCHER_SIZE - VOICE_LAUNCHER_MARGIN),
  };
};

const snapLauncherPosition = (
  x: number,
  y: number,
  bounds: VoiceLauncherBounds
): VoiceLauncherPosition => {
  const clampedX = clamp(x, bounds.minX, bounds.maxX);
  const clampedY = clamp(y, bounds.minY, bounds.maxY);

  const distances: Array<{ edge: VoiceLauncherEdge; distance: number }> = [
    { edge: 'left', distance: clampedX - bounds.minX },
    { edge: 'right', distance: bounds.maxX - clampedX },
    { edge: 'top', distance: clampedY - bounds.minY },
    { edge: 'bottom', distance: bounds.maxY - clampedY },
  ];
  const nearestEdge = distances.reduce((best, current) => (
    current.distance < best.distance ? current : best
  )).edge;

  if (nearestEdge === 'left') return { x: bounds.minX, y: clampedY, edge: 'left' };
  if (nearestEdge === 'right') return { x: bounds.maxX, y: clampedY, edge: 'right' };
  if (nearestEdge === 'top') return { x: clampedX, y: bounds.minY, edge: 'top' };
  return { x: clampedX, y: bounds.maxY, edge: 'bottom' };
};

const getDefaultLauncherPosition = (screen: 'lobby' | 'game'): VoiceLauncherPosition => {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const bounds = getLauncherBounds(screen);
  if (width > height) {
    return snapLauncherPosition(bounds.minX, 88, bounds);
  }
  return snapLauncherPosition(
    bounds.maxX,
    height - 240,
    bounds
  );
};

const getStoredLauncherPosition = (screen: 'lobby' | 'game'): VoiceLauncherPosition => {
  try {
    const raw = localStorage.getItem(VOICE_LAUNCHER_STORAGE_KEY);
    if (!raw) return getDefaultLauncherPosition(screen);
    const parsed = JSON.parse(raw) as Partial<VoiceLauncherPosition>;
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') {
      return getDefaultLauncherPosition(screen);
    }
    return snapLauncherPosition(parsed.x, parsed.y, getLauncherBounds(screen));
  } catch {
    return getDefaultLauncherPosition(screen);
  }
};

const saveLauncherPosition = (position: VoiceLauncherPosition) => {
  localStorage.setItem(VOICE_LAUNCHER_STORAGE_KEY, JSON.stringify(position));
};

interface VoiceChatProps {
  roomCode: string;
  playerName: string;
  screen: 'lobby' | 'game';
}

const VoiceChat: React.FC<VoiceChatProps> = ({ roomCode, playerName, screen }) => {
  const [inVoice, setInVoice] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const isConnectingRef = useRef(false);
  const [micOn, setMicOn] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [peerCount, setPeerCount] = useState(0);
  const [micError, setMicError] = useState<string | null>(null);
  const [playbackBlocked, setPlaybackBlocked] = useState(false);
  const [activeSpeakerNames, setActiveSpeakerNames] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(screen !== 'game');
  const [launcherPosition, setLauncherPosition] = useState<VoiceLauncherPosition>(() => getStoredLauncherPosition(screen));
  const micErrorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
    moved: boolean;
  } | null>(null);
  const suppressLauncherClickRef = useRef(false);

  const roomRef = useRef<Room | null>(null);
  const audioContainerRef = useRef<HTMLDivElement | null>(null);
  const audioUnlockedRef = useRef(false);
  const speakerOnRef = useRef(true);
  const autoJoinKeyRef = useRef<string | null>(null);

  const showMicError = useCallback((msg: string) => {
    setMicError(msg);
    if (micErrorTimerRef.current) clearTimeout(micErrorTimerRef.current);
    micErrorTimerRef.current = setTimeout(() => setMicError(null), 6000);
  }, []);

  const clearAudioElements = useCallback(() => {
    const container = audioContainerRef.current;
    if (!container) return;
    Array.from(container.children).forEach((child) => child.remove());
  }, []);

  const syncSpeakerState = useCallback((enabled: boolean) => {
    const container = audioContainerRef.current;
    if (!container) return;
    container.querySelectorAll('audio').forEach((el) => {
      (el as HTMLAudioElement).muted = !enabled;
    });
  }, []);

  const updatePeerCount = useCallback(() => {
    const room = roomRef.current;
    setPeerCount(room ? room.remoteParticipants.size : 0);
  }, []);

  const cleanupRoom = useCallback(() => {
    const room = roomRef.current;
    if (room) {
      room.removeAllListeners();
      room.disconnect();
      roomRef.current = null;
    }
    clearAudioElements();
    setPeerCount(0);
    setInVoice(false);
    setIsConnecting(false);
    setMicOn(false);
    setPlaybackBlocked(false);
    setActiveSpeakerNames([]);
  }, [clearAudioElements]);

  const unlockAudio = useCallback(() => {
    if (audioUnlockedRef.current) return;
    const a = new Audio(SILENT_WAV);
    a.play().then(() => {
      audioUnlockedRef.current = true;
    }).catch(() => {});
  }, []);

  const attachRemoteAudio = useCallback((track: Track) => {
    if (track.kind !== Track.Kind.Audio || !audioContainerRef.current) return;
    const element = track.attach() as HTMLAudioElement;
    element.autoplay = true;
    element.muted = !speakerOnRef.current;
    element.setAttribute('playsinline', 'true');
    audioContainerRef.current.appendChild(element);
  }, []);

  const detachRemoteAudio = useCallback((track: Track) => {
    if (track.kind !== Track.Kind.Audio) return;
    track.detach().forEach((element) => element.remove());
  }, []);

  const resumePlayback = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return false;
    try {
      await room.startAudio();
      const canPlay = room.canPlaybackAudio;
      setPlaybackBlocked(!canPlay);
      return canPlay;
    } catch {
      setPlaybackBlocked(true);
      return false;
    }
  }, []);

  const registerRoomEvents = useCallback((room: Room) => {
    room
      .on(RoomEvent.TrackSubscribed, (track) => {
        attachRemoteAudio(track);
      })
      .on(RoomEvent.TrackUnsubscribed, (track) => {
        detachRemoteAudio(track);
      })
      .on(RoomEvent.ParticipantConnected, () => {
        updatePeerCount();
      })
      .on(RoomEvent.ParticipantDisconnected, () => {
        updatePeerCount();
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        const remoteNames = speakers
          .filter((participant) => participant.identity !== room.localParticipant.identity)
          .map((participant) => participant.name || participant.identity)
          .slice(0, 2);
        setActiveSpeakerNames(remoteNames);
      })
      .on(RoomEvent.AudioPlaybackStatusChanged, (canPlayback) => {
        setPlaybackBlocked(!canPlayback);
      })
      .on(RoomEvent.ConnectionStateChanged, (state) => {
        if (state === ConnectionState.Reconnecting) {
          showMicError('Voice connection is reconnecting...');
        } else if (state === ConnectionState.Connected) {
          setMicError(null);
        }
      })
      .on(RoomEvent.Disconnected, () => {
        cleanupRoom();
      });
  }, [attachRemoteAudio, cleanupRoom, detachRemoteAudio, showMicError, updatePeerCount]);

  const joinVoice = useCallback(async (enableMicOnJoin = false) => {
    if (!roomCode || !playerName || roomRef.current || isConnectingRef.current) return;

    setMicError(null);
    isConnectingRef.current = true;
    setIsConnecting(true);
    if (enableMicOnJoin || playbackBlocked) {
      unlockAudio();
    }

    try {
      const room = new Room({
        adaptiveStream: false,
        dynacast: false,
        audioCaptureDefaults: AUDIO_CONSTRAINTS,
        disconnectOnPageLeave: false,
      });

      registerRoomEvents(room);

      const tokenData = await fetchLiveKitToken({
        roomCode,
        playerName,
        playerId: getPlayerId(),
      });

      await room.connect(tokenData.serverUrl, tokenData.token, {
        autoSubscribe: true,
        maxRetries: 3,
      });

      roomRef.current = room;

      if (enableMicOnJoin) {
        await room.localParticipant.setMicrophoneEnabled(true, AUDIO_CONSTRAINTS);
      }

      room.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          if (publication.track) attachRemoteAudio(publication.track);
        });
      });

      setInVoice(true);
      setMicOn(enableMicOnJoin);
      updatePeerCount();
      syncSpeakerState(speakerOnRef.current);
      await resumePlayback();
    } catch (err: any) {
      console.error('[Voice] LiveKit join failed:', err);
      cleanupRoom();

      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        showMicError('Mic blocked — allow microphone access in your browser, then try again');
      } else if (err?.name === 'NotFoundError') {
        showMicError('No microphone found on this device');
      } else {
        showMicError(err?.message || 'Could not join voice chat');
      }
    } finally {
      isConnectingRef.current = false;
      setIsConnecting(false);
    }
  }, [
    attachRemoteAudio,
    cleanupRoom,
    playbackBlocked,
    playerName,
    registerRoomEvents,
    resumePlayback,
    roomCode,
    showMicError,
    syncSpeakerState,
    unlockAudio,
    updatePeerCount,
  ]);

  const leaveVoice = useCallback(() => {
    cleanupRoom();
  }, [cleanupRoom]);

  const toggleMic = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;

    const enabled = !micOn;
    try {
      if (enabled) unlockAudio();
      await room.localParticipant.setMicrophoneEnabled(enabled, AUDIO_CONSTRAINTS);
      setMicOn(enabled);
    } catch (err) {
      console.error('[Voice] Failed to toggle microphone:', err);
      showMicError('Could not update microphone');
    }
  }, [micOn, showMicError, unlockAudio]);

  const toggleSpeaker = useCallback(() => {
    const enabled = !speakerOn;
    speakerOnRef.current = enabled;
    syncSpeakerState(enabled);
    setSpeakerOn(enabled);
    if (enabled) {
      unlockAudio();
      void resumePlayback();
    }
  }, [resumePlayback, speakerOn, syncSpeakerState, unlockAudio]);

  useEffect(() => {
    speakerOnRef.current = speakerOn;
  }, [speakerOn]);

  useEffect(() => {
    setIsExpanded(screen !== 'game');
  }, [screen]);

  useEffect(() => {
    if (screen !== 'game') return;
    setLauncherPosition((prev) => snapLauncherPosition(prev.x, prev.y, getLauncherBounds(screen)));
  }, [screen]);

  useEffect(() => {
    if (!roomCode || !playerName) return;
    const joinKey = `${roomCode}:${playerName}`;
    if (autoJoinKeyRef.current === joinKey || roomRef.current) return;
    autoJoinKeyRef.current = joinKey;
    void joinVoice(false);
  }, [joinVoice, playerName, roomCode]);

  useEffect(() => {
    if (!playbackBlocked || !speakerOn || !inVoice) return;
    const tryResume = () => {
      void resumePlayback();
    };
    window.addEventListener('pointerdown', tryResume, { passive: true });
    return () => window.removeEventListener('pointerdown', tryResume);
  }, [inVoice, playbackBlocked, resumePlayback, speakerOn]);

  useEffect(() => {
    if (screen !== 'game') return;
    const handleResize = () => {
      setLauncherPosition((prev) => {
        const next = snapLauncherPosition(prev.x, prev.y, getLauncherBounds(screen));
        saveLauncherPosition(next);
        return next;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [screen]);

  useEffect(() => {
    if (screen !== 'game') return;
    const handlePointerMove = (event: PointerEvent) => {
      const drag = dragStateRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const nextX = drag.originX + (event.clientX - drag.startX);
      const nextY = drag.originY + (event.clientY - drag.startY);
      const bounds = getLauncherBounds(screen);
      if (!drag.moved && (Math.abs(event.clientX - drag.startX) > 5 || Math.abs(event.clientY - drag.startY) > 5)) {
        drag.moved = true;
      }
      setLauncherPosition((prev) => ({
        ...prev,
        x: clamp(nextX, bounds.minX, bounds.maxX),
        y: clamp(nextY, bounds.minY, bounds.maxY),
      }));
    };

    const handlePointerUp = (event: PointerEvent) => {
      const drag = dragStateRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      if (drag.moved) {
        suppressLauncherClickRef.current = true;
        setLauncherPosition((prev) => {
          const snapped = snapLauncherPosition(prev.x, prev.y, getLauncherBounds(screen));
          saveLauncherPosition(snapped);
          return snapped;
        });
      }
      dragStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [screen]);

  useEffect(() => {
    return () => {
      if (micErrorTimerRef.current) clearTimeout(micErrorTimerRef.current);
      cleanupRoom();
    };
  }, [cleanupRoom]);

  const voiceStatusLabel = playbackBlocked
    ? 'Tap speaker to hear'
    : !speakerOn
      ? 'Speaker off'
      : activeSpeakerNames.length
        ? `${activeSpeakerNames[0]} talking`
        : isConnecting
          ? 'Connecting'
          : peerCount > 0
            ? 'Voice ready'
            : 'Waiting for players';

  const needsAttention = activeSpeakerNames.length > 0 && (!speakerOn || playbackBlocked);
  const launcherIcon = isConnecting
    ? '…'
    : !speakerOn
      ? '🔇'
      : micOn
        ? '🎙'
        : '🔊';
  const panelPlacement = launcherPosition.edge === 'left'
    ? 'right'
    : launcherPosition.edge === 'right'
      ? 'left'
      : launcherPosition.edge === 'top'
        ? 'bottom'
        : 'top';

  const voiceControls = (
    <>
      <div className={`voice-status-pill ${playbackBlocked ? 'warning' : activeSpeakerNames.length && speakerOn ? 'speaking' : ''}`}>
        {voiceStatusLabel}
      </div>
      <div className="voice-actions">
        <button
          className={`voice-btn ${micOn ? 'active' : 'muted'}`}
          onClick={(event) => {
            event.stopPropagation();
            void toggleMic();
          }}
          aria-label={micOn ? 'Mute mic' : 'Unmute mic'}
          title={micOn ? 'Mute mic' : 'Unmute mic'}
        >
          {micOn ? '🎙' : '🎤'}
        </button>
        <button
          className={`voice-btn ${speakerOn ? 'active' : 'muted'} ${activeSpeakerNames.length && speakerOn ? 'speaking' : ''}`}
          onClick={(event) => {
            event.stopPropagation();
            toggleSpeaker();
          }}
          aria-label={speakerOn ? 'Mute speaker' : 'Unmute speaker'}
          title={speakerOn ? 'Mute speaker' : 'Unmute speaker'}
        >
          {speakerOn ? '🔊' : '🔇'}
        </button>
        {screen === 'game' ? (
          <button
            className="voice-btn voice-collapse-btn"
            onClick={(event) => {
              event.stopPropagation();
              setIsExpanded(false);
            }}
            aria-label="Close voice controls"
            title="Close voice controls"
          >
            ✕
          </button>
        ) : (
          <button
            className="voice-btn voice-leave-btn"
            onClick={(event) => {
              event.stopPropagation();
              leaveVoice();
            }}
            aria-label="Leave voice chat"
            title="Leave voice chat"
          >
            ✕
          </button>
        )}
        {peerCount > 0 && (
          <span className="voice-peer-count" aria-label={`${peerCount} other ${peerCount === 1 ? 'person' : 'people'} in voice`}>{peerCount}</span>
        )}
      </div>
    </>
  );

  return (
    <div
      className={`voice-chat ${screen === 'game' ? 'in-game' : 'in-lobby'} ${activeSpeakerNames.length ? 'has-activity' : ''}`}
      style={screen === 'game' ? { left: launcherPosition.x, top: launcherPosition.y } : undefined}
    >
      <div ref={audioContainerRef} className="voice-audio-container" aria-hidden="true" />
      <AnimatePresence>
        {micError && (
          <motion.div
            key="mic-error"
            className="voice-mic-error"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            onClick={() => setMicError(null)}
          >
            {micError}
          </motion.div>
        )}
      </AnimatePresence>
      {screen === 'game' ? (
        <>
          <button
            className={`voice-launcher ${isExpanded ? 'expanded' : ''} ${needsAttention ? 'needs-attention' : ''}`}
            onPointerDown={(event) => {
              dragStateRef.current = {
                pointerId: event.pointerId,
                startX: event.clientX,
                startY: event.clientY,
                originX: launcherPosition.x,
                originY: launcherPosition.y,
                moved: false,
              };
            }}
            onClick={() => {
              if (suppressLauncherClickRef.current) {
                suppressLauncherClickRef.current = false;
                return;
              }
              setIsExpanded((prev) => !prev);
            }}
            title={isExpanded ? 'Hide voice controls' : 'Show voice controls'}
          >
            <span className="voice-launcher-icon">{launcherIcon}</span>
            {peerCount > 0 && <span className="voice-launcher-badge">{peerCount}</span>}
          </button>
          <AnimatePresence>
            {isExpanded && (
              <div className={`voice-controls-floating placement-${panelPlacement}`}>
                <motion.div
                  key="game-controls"
                  className="voice-controls"
                  onPointerDown={(event) => event.stopPropagation()}
                  initial={{ scale: 0.92, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.92, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {!inVoice ? (
                    <button
                      className="voice-btn voice-join-btn"
                      onClick={(event) => {
                        event.stopPropagation();
                        void joinVoice(true);
                      }}
                      title={isConnecting ? 'Connecting voice chat' : 'Join voice chat with microphone'}
                      disabled={isConnecting}
                    >
                      {isConnecting ? '…' : '🎙'}
                    </button>
                  ) : voiceControls}
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </>
      ) : (
        <AnimatePresence mode="wait">
          {!inVoice ? (
            <motion.button
              key="join"
              className="voice-btn voice-join-btn"
              onClick={() => { void joinVoice(true); }}
              title={isConnecting ? 'Connecting voice chat' : 'Join voice chat with microphone'}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.15 }}
              disabled={isConnecting}
            >
              {isConnecting ? '…' : '🎙'}
            </motion.button>
          ) : (
            <motion.div
              key="controls"
              className="voice-controls"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {voiceControls}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default VoiceChat;
